import type { PropCatalog, PropCategory, PropDefinition } from '@/site/PropCatalog';
import { propCategoryLabel } from '@/site/PropCatalog';
import { clear, el, formatLength, setClass } from './dom';
import { icon } from './Icons';
import { objectThumbnailSvg } from './ObjectThumbnail';
import type { UiPreferences } from './UiPreferences';

/**
 * The objects palette — benches, boulders, lamps, fire pits. The third placeable class.
 *
 * This is the panel `docs/PROPS.md` has been asking for since the prop library shipped: everything
 * under it was already built and tested, and the only thing standing between a designer and a bench
 * was that nothing on screen listed one. Which is why this file is short — it browses a catalog that
 * already answers every question it needs to ask (`search`, `categories`, `propCategoryLabel`), and
 * arms a tool that already knows how to place one.
 *
 * ## Licence is shown, not buried
 *
 * Every record here is a third-party asset redistributed inside the application, and the catalog
 * refuses to load one without attribution. Putting the author and licence in the tile's tooltip
 * costs nothing and means the credit is where someone choosing the asset will actually see it —
 * rather than only in a credits panel nobody opens. See `src/assets/props/NOTICES.md`.
 */

export interface ObjectBrowserDeps {
  catalog: PropCatalog;
  preferences: UiPreferences;
  /** Arm placement with a catalog prop. */
  onPick: (propId: string) => void;
  /** What is armed right now, so the palette can show it selected. */
  armed?: () => string | null;
}

export class ObjectBrowser {
  readonly root: HTMLElement;

  private search = '';
  private activeCategory: PropCategory | 'all' = 'all';

  private readonly searchInput: HTMLInputElement;
  private readonly railEl: HTMLElement;
  private readonly resultsEl: HTMLElement;
  private readonly countEl: HTMLElement;

  constructor(private readonly deps: ObjectBrowserDeps) {
    this.countEl = el('span', { class: 'lg-panel__count' });
    this.searchInput = el('input', {
      class: 'lg-search__input',
      attrs: {
        type: 'search',
        placeholder: 'Search objects, tags…',
        'aria-label': 'Search the object catalog',
      },
      on: {
        input: () => {
          this.search = this.searchInput.value;
          this.renderResults();
        },
      },
    });

    this.railEl = el('div', {
      class: 'lg-rail',
      attrs: { role: 'group', 'aria-label': 'Object category' },
    });
    this.resultsEl = el('div', { class: 'lg-browser__results' });

    this.root = el(
      'aside',
      {
        class: 'lg-panel lg-panel--left lg-panel--palette',
        attrs: { 'aria-label': 'Object palette' },
      },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon', html: icon('object', 16) }),
        el('h2', { class: 'lg-panel__title', text: 'Objects' }),
        this.countEl,
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('chevron', 16),
          title: 'Shut the object palette',
          attrs: { type: 'button', 'aria-label': 'Shut the object palette' },
          on: { click: () => this.setShut(!this.isShut) },
        }),
      ),
      el(
        'div',
        { class: 'lg-panel__body' },
        el(
          'div',
          { class: 'lg-search' },
          el('span', { class: 'lg-search__icon', html: icon('search', 16) }),
          this.searchInput,
        ),
        this.railEl,
        this.resultsEl,
      ),
    );

    this.setShut(deps.preferences.panelShut('objects'));
    this.renderRail();
    this.renderResults();
  }

  get isShut(): boolean {
    return this.root.classList.contains('is-shut');
  }

  setShut(shut: boolean): void {
    setClass(this.root, 'is-shut', shut);
    this.deps.preferences.setPanelShut('objects', shut);
  }

  refresh(): void {
    this.renderResults();
  }

  /* ---------------------------------- Rendering ---------------------------------- */

  private renderRail(): void {
    clear(this.railEl);
    const chip = (label: string, value: PropCategory | 'all', count: number): HTMLElement =>
      el(
        'button',
        {
          class: `lg-chip${this.activeCategory === value ? ' is-active' : ''}`,
          attrs: { type: 'button', 'aria-pressed': this.activeCategory === value },
          on: {
            click: () => {
              this.activeCategory = value;
              this.renderRail();
              this.renderResults();
            },
          },
        },
        el('span', { text: label }),
        el('span', { class: 'lg-chip__count', text: String(count) }),
      );

    this.railEl.appendChild(chip('All', 'all', this.deps.catalog.size));
    // Only categories with something in them — the catalog declares twelve and ships seven.
    for (const category of this.deps.catalog.categories()) {
      this.railEl.appendChild(
        chip(
          propCategoryLabel(category),
          category,
          this.deps.catalog.byCategory(category).length,
        ),
      );
    }
  }

  private matches(): PropDefinition[] {
    const found = this.deps.catalog.search(this.search);
    if (this.activeCategory === 'all') return found;
    return found.filter((prop) => prop.category === this.activeCategory);
  }

  private renderResults(): void {
    clear(this.resultsEl);
    const results = this.matches();
    this.countEl.textContent = String(results.length);

    if (results.length === 0) {
      this.resultsEl.appendChild(
        el('div', { class: 'lg-empty' }, el('p', { text: 'Nothing matches that.' })),
      );
      return;
    }

    const groups = new Map<PropCategory, PropDefinition[]>();
    for (const prop of results) {
      const list = groups.get(prop.category) ?? [];
      list.push(prop);
      groups.set(prop.category, list);
    }

    for (const [category, members] of groups) {
      const section = el('section', { class: 'lg-group' });
      if (groups.size > 1) {
        section.appendChild(
          el(
            'h3',
            { class: 'lg-group__title' },
            el('span', { text: propCategoryLabel(category) }),
            el('span', { class: 'lg-group__count', text: String(members.length) }),
          ),
        );
      }
      const grid = el('div', { class: 'lg-grid' });
      for (const prop of members) grid.appendChild(this.tile(prop));
      section.appendChild(grid);
      this.resultsEl.appendChild(section);
    }
  }

  private tile(prop: PropDefinition): HTMLElement {
    const armed = this.deps.armed?.() === prop.id;
    const unit = this.deps.preferences.unit;
    return el(
      'button',
      {
        class: `lg-tile${armed ? ' is-active' : ''}`,
        title: `${prop.name} · ${prop.license.author}, ${prop.license.spdx}`,
        attrs: { type: 'button' },
        on: { click: () => this.pick(prop.id) },
      },
      el('span', { class: 'lg-tile__art', html: objectThumbnailSvg(prop) }),
      el('span', { class: 'lg-tile__name', text: prop.name }),
      el('span', {
        class: 'lg-tile__meta',
        // Width × height: the two a designer is checking against the space they have.
        text: `${formatLength(prop.size.widthMeters, unit)} × ${formatLength(prop.size.heightMeters, unit)}`,
      }),
    );
  }

  private pick(propId: string): void {
    this.deps.onPick(propId);
    this.renderResults();
  }
}
